import {
	AddToCartButton,
	AddToCartContent,
	AddToCartQuantityField,
	BackorderBannerWrapper,
	DetailsPageContainer,
	DetailsPageContent,
	ItemDetails
} from "features/MarketingMaterials/pages/ItemDetails/MarketingMaterialsDetailsPage.styles.ts";
import { PageHeader } from "features/MarketingMaterials/pages/components/PageHeader/PageHeader.tsx";
import { useNavigate, useParams } from "react-router";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import { useGetMerchandiseProductByIdQuery } from "features/MarketingMaterials/api/merchProductsApi.ts";
import { formatPriceWithPrefix } from "utils/string.ts";
import { ItemImageCarousel } from "features/MarketingMaterials/pages/ItemDetails/components/ItemImageCarousel.tsx";
import { useState } from "react";
import useMarketingMaterialsCart from "features/MarketingMaterials/hooks/useMarketingMaterialsCart.ts";
import { ItemDetailsSkeleton } from "features/MarketingMaterials/pages/ItemDetails/components/ItemDetailsSkeleton.tsx";
import { MarketingMaterialsSearchBar } from "features/MarketingMaterials/pages/components/MarketingMaterialsSearchBar/MarketingMaterialsSearchBar.tsx";
import {
	MARKETING_MATERIALS_SEARCH_KEY,
	MarketingMaterialsSearchButton,
	MarketingMaterialsSearchHelperText,
	MarketingMaterialsSearchPlaceholder
} from "../marketingMaterialsConstants.ts";
import {
	canAddMerchandiseToCart,
	getMerchandiseInventoryDisplay
} from "features/MarketingMaterials/utils/getMerchandiseInventoryStatus.ts";
import InfoBanner from "components/Common/InfoBanner/InfoBanner.tsx";
import { getMerchandiseImageUrls } from "features/MarketingMaterials/utils/getMerchandiseImageUrls.ts";
import useSession from "hooks/useSession.ts";
import { AvailabilityStatus } from "types/declarations/enums.ts";

export const MarketingMaterialsDetailsPage = () => {
	const params = useParams();
	const [quantity, setQuantity] = useState("1");
	const { addCartItem, cartData } = useMarketingMaterialsCart();

	const { data, isLoading } = useGetMerchandiseProductByIdQuery(
		{ productId: params.productId ?? "" },
		{ skip: !params.productId }
	);

	const navigate = useNavigate();
	const [searchValue, setSearchValue] = useSession<string>(MARKETING_MATERIALS_SEARCH_KEY, "");

	const handleSearch = () => {
		const term = searchValue.trim();
		navigate({
			pathname: "/marketing-materials",
			search: term ? `?term=${encodeURIComponent(term)}` : ""
		});
	};

	const handleClear = () => setSearchValue("");

	const handleQuantityChange = (value: string) => {
		const sanitizedValue = value.replace(/\D/g, "").slice(0, 4);
		setQuantity(sanitizedValue);
	};

	const handleQuantityBlur = () => {
		if (!quantity) {
			setQuantity("0");
		}
	};

	const handleAddToCartButton = () => {
		addCartItem(data!, parseInt(quantity));
	};

	const stockDisplay = getMerchandiseInventoryDisplay(data);
	const canAddToCart = canAddMerchandiseToCart(data);
	const productImages = getMerchandiseImageUrls(data?.images);

	const getBackorderBanner = () => {
		if (data && data.availabilityStatus == AvailabilityStatus.Backordered) {
			return (
				<BackorderBannerWrapper>
					<InfoBanner
						title="This item is on backorder"
						bodyNode={
							<p>
								You can still order this item, but shipping will be delayed until it is back in stock.
							</p>
						}
					/>
				</BackorderBannerWrapper>
			);
		}

		return <p>{stockDisplay}</p>;
	};

	return (
		<DetailsPageContainer>
			<PageHeader cartData={cartData} />
			{isLoading ? (
				<ItemDetailsSkeleton />
			) : (
				<>
					<MarketingMaterialsSearchBar
						value={searchValue}
						placeholder={MarketingMaterialsSearchPlaceholder}
						helperText={MarketingMaterialsSearchHelperText}
						searchButtonLabel={MarketingMaterialsSearchButton}
						onChange={setSearchValue}
						onSearch={handleSearch}
						onClear={handleClear}
					/>
					<DetailsPageContent>
						{data && <ItemImageCarousel images={productImages} />}
						<ItemDetails>
							<h3>{data?.name}</h3>
							<p>{data?.description}</p>
							<p>{data?.sku}</p>
							<h3>{formatPriceWithPrefix(data?.listPrice, false, true)}</h3>
							<AddToCartContent>
								<AddToCartQuantityField
									variant="outlined"
									size="small"
									label="Quantity"
									placeholder="Enter quantity"
									type="text"
									value={quantity}
									onChange={(event) => handleQuantityChange(event.target.value)}
									onBlur={handleQuantityBlur}
									slotProps={{
										input: {
											inputProps: {
												inputMode: "numeric",
												pattern: "[0-9]*",
												maxLength: 3,
												min: 0
											}
										}
									}}
								/>
								{canAddToCart && (
									<AddToCartButton
										variant="contained"
										startIcon={<AddShoppingCartIcon />}
										onClick={handleAddToCartButton}
									>
										Add to Cart
									</AddToCartButton>
								)}
							</AddToCartContent>
							{stockDisplay && getBackorderBanner()}
						</ItemDetails>
					</DetailsPageContent>
				</>
			)}
		</DetailsPageContainer>
	);
};
